// A value chosen on the page flies to where the choices are: the element clicked is remembered, and the
// choice that appears for it animates from there (FLIP with a ghost element).

let last: { el: Element; at: number } | null = null;
let pending: { key: string; rect: DOMRect; background: string; color: string; radius: string; at: number } | null = null;

if (typeof document !== 'undefined') {
  document.addEventListener('pointerdown', (e) => {
    const el = (e.target as Element | null)?.closest?.('[data-fly]');
    if (el) last = { el, at: Date.now() };
  }, true);
}

const reduced = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** The choice `key` is being made from the element just clicked. */
export function launch(key: string): void {
  if (!last || Date.now() - last.at > 1500 || reduced()) return;
  const el = last.el as HTMLElement;
  const cs = getComputedStyle(el);
  pending = {
    key, rect: el.getBoundingClientRect(), at: Date.now(),
    background: cs.backgroundImage !== 'none' ? cs.backgroundImage : cs.backgroundColor, color: cs.color, radius: cs.borderTopLeftRadius,
  };
  el.animate([{ opacity: 1, transform: 'scale(1)' }, { opacity: 0.25, transform: 'scale(.94)' }], { duration: 260, easing: 'ease-out', fill: 'forwards' });
  last = null;
}

/** The element of choice `key` has appeared: if it was launched, fly there. */
export function land(key: string, target: HTMLElement | null): void {
  if (!target || !pending || pending.key !== key || Date.now() - pending.at > 8000) return;
  const from = pending;
  pending = null;
  const to = target.getBoundingClientRect();
  if (to.width === 0) return;
  const ghost = target.cloneNode(true) as HTMLElement;
  const ts = getComputedStyle(target);
  Object.assign(ghost.style, {
    position: 'fixed', left: `${from.rect.left}px`, top: `${from.rect.top}px`, width: `${from.rect.width}px`, height: `${from.rect.height}px`,
    margin: '0', zIndex: '100', pointerEvents: 'none', overflow: 'hidden', boxSizing: 'border-box',
  });
  document.body.appendChild(ghost);
  target.style.opacity = '0';
  const anim = ghost.animate([
    { left: `${from.rect.left}px`, top: `${from.rect.top}px`, width: `${from.rect.width}px`, height: `${from.rect.height}px`,
      borderRadius: from.radius, background: from.background, color: from.color, boxShadow: '0 30px 60px -20px rgba(40,50,140,.45)', opacity: 0.95 },
    { offset: 0.55, borderRadius: ts.borderTopLeftRadius, opacity: 1 },
    { left: `${to.left}px`, top: `${to.top}px`, width: `${to.width}px`, height: `${to.height}px`,
      borderRadius: ts.borderTopLeftRadius, background: ts.backgroundColor, color: ts.color, boxShadow: '0 4px 12px -4px rgba(0,0,0,.3)', opacity: 1 },
  ], { duration: 620, easing: 'cubic-bezier(.2,.8,.2,1)' });
  anim.onfinish = () => {
    ghost.remove();
    target.style.opacity = '';
    target.animate([{ transform: 'scale(1.08)' }, { transform: 'scale(1)' }], { duration: 260, easing: 'ease-out' });
  };
}

/** Animates an element away before it is removed. */
export function leave(el: HTMLElement | null, then: () => void): void {
  if (!el || reduced()) return then();
  el.animate([{ opacity: 1, transform: 'scale(1)' }, { opacity: 0, transform: 'scale(.6)' }], { duration: 200, easing: 'ease-in', fill: 'forwards' })
    .onfinish = then;
}
